import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { MILES_NEARYBY_LESSONS } from 'constants/seo';
import { SportsEnum } from 'types/generated/client';
import { GetCityCoachesQuery } from 'types/generated/server';
import { getAllCityVenuePaths } from 'services/server/graphql/queries/getAllCityVenuePaths';
import { getCityCoaches } from 'services/server/graphql/queries/getCityCoaches';
import { getCityPickleballVenuesByCitySlug } from 'services/server/graphql/queries/getCityVenuesByCitySlug';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generatePlaceJsonLd } from 'utils/shared/json-ld/place';
import { generateLessonsBreadcrumbs } from 'screens/CityVenuesPage/utils';
import ExploreLessons from 'screens/ExploreLessons';
import { LessonPageModeEnum } from 'screens/ExploreLessons/enums';
import CityLocationProvider from 'components/utilities/CityLocationProvider';

const REVALIDATE_IN_SECONDS = 6 * 60 * 60 * 1; // 6 hours
const IS_ALL_AS_ISR = true;
const SPORT = SportsEnum.Pickleball;

export interface Props {
  citySlug: string;
  cityData:
    | NonNullable<Awaited<ReturnType<typeof getCityPickleballVenuesByCitySlug>>>['cities'][0]
    | null;
  coaches: GetCityCoachesQuery['userProfiles'];
  jsonLd?: string;
}

export const getStaticProps: GetStaticProps<Props, { citySlug: string }> = async ({ params }) => {
  const { citySlug } = params || {};

  if (!citySlug) {
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  try {
    // Fetch city data using existing query pattern
    const cityVenueResponse = await getCityPickleballVenuesByCitySlug({ slug: citySlug });
    const cityData = cityVenueResponse?.cities[0];

    if (!cityData || !cityData?.id || !cityData?.name) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    // Fetch coaches for this city and sport using radius-based search
    const sport = SportsEnum.Pickleball;
    const searchRadius = milesToMeters(MILES_NEARYBY_LESSONS);
    const coachesResponse = await getCityCoaches({
      distance: searchRadius,
      from: {
        type: 'Point',
        coordinates: [cityData.longitude, cityData.latitude],
      },
      sport,
      defaultSport: sport as string,
      limit: 1000,
    });

    const coaches = coachesResponse?.userProfiles || [];

    // Check if we have coaches for this sport in this city
    if (coaches.length === 0) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    // Generate JSON-LD for breadcrumbs and place information
    const cityPlaceJsonLd = generatePlaceJsonLd({
      name: cityData.name,
      geo: {
        latitude: cityData.latitude,
        longitude: cityData.longitude,
      },
    });

    const breadcrumbs = generateBreadcrumbListJsonLd({
      items: generateLessonsBreadcrumbs({
        cityName: cityData?.name || '',
        citySlug: cityData?.slug || '',
        regionName: cityData?.countrySubdivision?.name || '',
        regionSlug: cityData?.countrySubdivision?.slug || '',
        countryId: cityData?.countrySubdivision?.country?.id || '',
        countryName: cityData?.countrySubdivision?.country?.name || '',
        countrySlug: cityData?.countrySubdivision?.country?.slug || '',
        sport: SPORT.toLowerCase(),
      }).jsonLd,
    });

    const combinedSchemas = combineJsonLdSchemas([breadcrumbs, cityPlaceJsonLd]);

    return {
      props: {
        citySlug,
        cityData,
        coaches,
        jsonLd: JSON.stringify(combinedSchemas),
      },
      revalidate: REVALIDATE_IN_SECONDS,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (IS_ALL_AS_ISR) {
    return {
      paths: [],
      fallback: true,
    };
  }

  try {
    const citiesData = await getAllCityVenuePaths();
    const paths = [];

    for (const city of citiesData.cities) {
      // Only include cities that have venues (which indicates active cities)
      if (city.venues.length > 0) {
        paths.push({
          params: {
            citySlug: city.slug,
          },
        });
      }
    }

    return {
      paths,
      fallback: true,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      paths: [],
      fallback: true,
    };
  }
};

const PickleballCityLessonsPage = (props: Props) => {
  if (!props.cityData) {
    return null; // This will be handled by Next.js fallback
  }

  // Generate breadcrumbs for UI display
  const breadcrumbs = generateLessonsBreadcrumbs({
    cityName: props.cityData?.name || '',
    citySlug: props.cityData?.slug || '',
    regionName: props.cityData?.countrySubdivision?.name || '',
    regionSlug: props.cityData?.countrySubdivision?.slug || '',
    countryId: props.cityData?.countrySubdivision?.country?.id || '',
    countryName: props.cityData?.countrySubdivision?.country?.name || '',
    countrySlug: props.cityData?.countrySubdivision?.country?.slug || '',
    sport: SPORT.toLowerCase(),
  }).ui;

  return (
    <CityLocationProvider
      city={{
        ...props.cityData,
        state: props.cityData.countrySubdivision?.name,
        country: props.cityData.countrySubdivision?.country?.id,
      }}
    >
      <ExploreLessons
        pageMode={LessonPageModeEnum.EXPLORE}
        initialSport={SPORT}
        cityData={props.cityData}
        coaches={props.coaches}
        breadcrumbs={breadcrumbs}
        jsonLd={props.jsonLd}
        title={`Pickleball Lessons Near Me in ${props.cityData.name}${
          props.cityData.countrySubdivision?.name
            ? `, ${props.cityData.countrySubdivision?.name}`
            : ''
        }`}
        subtitle={`Find the best pickleball coach near me in ${props.cityData.name}`}
      />
    </CityLocationProvider>
  );
};

export default PickleballCityLessonsPage;
